import React, { useState, useRef } from 'react';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { Checkbox } from '@progress/kendo-react-inputs';
import { Button } from '@progress/kendo-react-buttons';
import { Input } from '@progress/kendo-react-inputs';
import ErrorBanner from "../../components/ErrorBanner";
import { useMutateSubmitModel, acceptedDisclosure } from "../../data/services/clientService";
import { Loader } from '@progress/kendo-react-indicators';
import '@progress/kendo-theme-default/dist/all.css';

const brokerBannerStyle = {
    background: '#fff3cd',
    border: '1px solid #ffc107',
    borderRadius: '4px',
    padding: '10px 14px',
    marginBottom: '14px',
    fontSize: '13px',
    color: '#856404',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
};

const proxyBoxStyle = {
    background: '#f0f4ff',
    border: '1px solid #4a6cf7',
    borderRadius: '4px',
    padding: '10px 14px',
    marginBottom: '14px',
    fontSize: '13px',
    color: '#1a1a1a',
};

const hashFingerprint = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = (Math.imul(31, hash) + str.charCodeAt(i)) | 0;
    }
    return Math.abs(hash).toString(16);
};

const DisclosureModal = ({
    clientId,
    clientName,
    oePeriodId,
    versionId,
    onClose,
    oePeriod,
    onAccept,
    loggedInUser,
    isBuiltByBroker = false,
    isOpen = true,
}) => {
    const [isSubmitted, setIsSubmitted] = React.useState(false);
    const [isDownloaded, setIsDownloaded] = React.useState(false);
    const [loading, setLoading] = useState(false);
    const [accepted, setAccepted] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [downloadError, setDownloadError] = useState(false);

    const [isProxy, setIsProxy] = useState(false);
    const [proxyAuthorizedBy, setProxyAuthorizedBy] = useState('');

    const useMutateSubmitModelHandler = useMutateSubmitModel();
    const openedAt = useRef(Date.now());

    if (!isOpen) return null;

    const isAdmin = loggedInUser?.hasAdminAppPrivilege ?? false;
    const isBroker = loggedInUser?.brokerUserPrivilege ?? false;

    const year = oePeriod?.effectiveDate
        ? new Date(oePeriod.effectiveDate).getFullYear()
        : new Date().getFullYear();

    const getActorRole = () => {
        if (isAdmin) return 'Admin';
        if (isBroker) return 'Broker';
        return 'Client';
    };

    const onSubmitConfirmClick = async () => {
        await useMutateSubmitModelHandler.mutateAsync({
            oePeriodId,
            clientId,
            versionId,
        });
        onClose();
    };

    const saveDisclosureAccept = async () => {
        if (!clientId || !oePeriodId) {
            throw new Error('Missing required fields: clientId and oePeriodId must be provided.');
        }

        const ua = navigator.userAgent;
        const lang = navigator.language;
        const platform = navigator.userAgentData?.platform ?? '';
        const mobile = navigator.userAgentData?.mobile ?? false;
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const deviceFingerprint = hashFingerprint([ua, lang, platform, String(mobile), timezone].join('|'));
        const timeToAcceptSeconds = Math.round((Date.now() - openedAt.current) / 1000);

        const inputData = {
            oePeriodId,
            clientId,
            clientName,
            versionId,
            downloaded: isDownloaded ? 1 : 0,
            disclosureAccepted: 1,
            userAgent: ua,
            browserLanguage: lang,
            platform,
            mobile,
            timezone,
            deviceFingerprint,
            timeToAcceptSeconds,
            firstName: loggedInUser?.firstName ?? '',
            lastName: loggedInUser?.lastName ?? '',
            eeId: loggedInUser?.eeId ?? '',
            userType: loggedInUser?.userType ?? '',
            actorRole: getActorRole(),
            isInternalUser: isAdmin,
            isProxyAcknowledgment: isProxy,
            proxyAuthorizedBy: isProxy ? (proxyAuthorizedBy.trim() || loggedInUser?.userId || '') : null,
        };

        const saveResult = await acceptedDisclosure(inputData);
        if (saveResult.status !== 200) {
            throw new Error(`Disclosure save failed with status ${saveResult.status}.`);
        }
        // Disclosure saved — caller is responsible for the model-submit step.
    };

    const handleDownloadDisclosure = async () => {
        setDownloadError(false);
        try {
            setIsDownloaded(true);
            const response = await fetch(`/disclosures/disclosure-agreement-${year}.pdf`);
            if (!response.ok) throw new Error('Failed to download');

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `disclosure-agreement-${year}.pdf`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            setIsDownloaded(false);
            setDownloadError(true);
        }
    };

    const handleAccept = async () => {
        if (!accepted || loading) return;
        if (isProxy && !proxyAuthorizedBy.trim()) return;
        setSaveError(null);
        setLoading(true);
        let disclosureSaved = false;
        try {
            await saveDisclosureAccept();
            disclosureSaved = true;
            await onSubmitConfirmClick();
            onAccept();
        } catch (err) {
            if (disclosureSaved) {
                setSaveError(
                    'Your disclosure was recorded but the model submission failed. ' +
                    'Please refresh the page and try submitting again.'
                );
            } else {
                setSaveError('Failed to save your disclosure. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    const proxyButtonDisabled = !accepted || loading || (isProxy && !proxyAuthorizedBy.trim());

    return (
        <Dialog title="Disclosure Agreement" onClose={onClose}>
            <ErrorBanner
                handler={useMutateSubmitModelHandler}
                onError={() => setIsSubmitted(false)}
            />

            {saveError && (
                <div style={{ padding: '0.5rem 1rem 0' }}>
                    <ErrorBanner message={saveError} />
                </div>
            )}

            {downloadError && (
                <div style={{ padding: '0.5rem 1rem 0' }}>
                    <ErrorBanner message="Failed to download the disclosure. Please try again." />
                </div>
            )}

            <div style={{ padding: '1rem', maxHeight: '460px', overflowY: 'auto', width: '80%', maxWidth: '620px', margin: '0 auto' }}>

                {isBuiltByBroker && (
                    <div style={brokerBannerStyle}>
                        <span style={{ fontSize: '16px' }}>&#9888;</span>
                        <div>
                            <strong>This model was built on your behalf by your broker.</strong>
                            <br />
                            Please review it carefully before acknowledging. By completing this acknowledgment, you confirm that you have reviewed and approve the selections your broker made.
                        </div>
                    </div>
                )}

                <div style={{ alignContent: 'flex-start' }}>
                    <h4>Contribution Model Submission Acknowledgment:</h4>
                    <p>
                        Before submitting your contribution model, please carefully review and acknowledge the following:
                    </p>
                    <p>
                        As agreed to under the Client Service Agreement between Prestige and Client, Prestige will provide certain employee benefits to eligible Covered Employees and their eligible dependents through Prestige-sponsored plans ("Prestige Benefit Plans") and administer such Prestige Benefit Plans in compliance with applicable law and the terms and provisions of the applicable plan documents. All capitalized terms not defined within this acknowledgment have the meanings set forth in the Client Service Agreement.
                    </p>
                    <p>
                        It is Client's responsibility to confirm the information contained in the contribution model, including but not limited to premium and contribution rates, benefit classes, elections, calculations and deductions.
                    </p>
                    <p>
                        The person submitting the contribution model acknowledges and agrees that (1) they are an authorized representative of Client; (2) Client is responsible for approving the premiums and contribution rates associated with the selected benefit plans; (3) Client has reviewed, approved and agrees with the selections and details as set forth in the contribution model; (4) Client has exercised independent judgment in selecting and approving the benefit plans that will be made available to employees, and has reviewed and approved all services and fees and other amounts related to such services; (5) Client is ultimately responsible for payment of all premiums or costs associated with any benefit program; (6) Prestige reserves the right to adjust premium rates at enrollment in the event of a material change in enrollment from the time benefits were originally quoted; and (7) Client has received and reviewed the Disclosures linked below.
                    </p>
                    <p>
                        <span style={{ textDecoration: 'underline', color: 'blue', cursor: 'pointer' }} onClick={handleDownloadDisclosure}>Disclosures</span>
                    </p>
                    <p style={{ fontStyle: 'italic' }}>
                        <span style={{ fontStyle: 'italic' }}>Privacy Notice:</span> By submitting this form, I acknowledge that Prestige collects the information described in its{' '}
                        <a
                            href="https://www.prestigepeo.com/consumer-privacy-act/"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: 'blue', textDecoration: 'underline' }}
                        >
                            Privacy Policy
                        </a>{' '}
                        for the purposes described therein. Prestige does not sell or share this information for monetary consideration; see the full Privacy Policy for additional information and your right to opt out.
                    </p>
                </div>

                <Checkbox
                    label="I, on behalf of Client, confirm receipt of the information and disclosures provided above and hereby approve, for Client's benefit plans, the insurance coverages, premium rates, and employee contribution strategy as outlined in the contribution model. Client acknowledges and agrees that Client is responsible for approving the premiums and contribution rates associated with the benefit plans made available to employees, including any plan-related service fees (as defined and set forth in the linked disclosures). Client acknowledges and agrees that if Client does not submit this acknowledgment, its worksite employees will not have access to Prestige plans or benefits. If any worksite employees are currently enrolled in Prestige plans or benefits, they will be deemed ineligible for continued coverage after the end of the current coverage period. For clarity, no other aspect of Prestige's services to Client will be affected by such non-acknowledgment."
                    checked={accepted}
                    onChange={(e) => setAccepted(e.value)}
                />

                {isAdmin && (
                    <div style={{ marginTop: '14px' }}>
                        <Checkbox
                            label="I am recording this acknowledgment on behalf of the client (proxy). This is an exception and requires authorization."
                            checked={isProxy}
                            onChange={(e) => {
                                setIsProxy(e.value);
                                if (!e.value) setProxyAuthorizedBy('');
                            }}
                        />
                        {isProxy && (
                            <div style={{ ...proxyBoxStyle, marginTop: '8px' }}>
                                <p style={{ margin: '0 0 8px 0', fontWeight: 600 }}>Proxy Acknowledgment</p>
                                <p style={{ margin: '0 0 8px 0', fontSize: '12px' }}>
                                    Proxy acknowledgment is an exception, not the norm. This record will be flagged in the audit trail with your identity as the acknowledging actor.
                                </p>
                                <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: 600 }}>
                                    Authorized by (name or ID):
                                </label>
                                <Input
                                    value={proxyAuthorizedBy}
                                    onChange={(e) => setProxyAuthorizedBy(e.value)}
                                    placeholder="Enter authorizing party name or ID"
                                    style={{ width: '100%' }}
                                />
                            </div>
                        )}
                    </div>
                )}
            </div>

            <DialogActionsBar>
                <Button
                    primary={true}
                    disabled={proxyButtonDisabled}
                    onClick={handleAccept}
                >
                    {loading ? (
                        <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <Loader type={"infinite-spinner"} />
                            Please wait...
                        </span>
                    ) : (
                        'Continue'
                    )}
                </Button>
            </DialogActionsBar>
        </Dialog>
    );
};

export default DisclosureModal;
